import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  useEffect,
  useState,
  type Dispatch,
  type FormEvent,
  type SetStateAction,
} from "react";

import { useNavigate, useSearch } from "@tanstack/react-router";

import { isApiClientError } from "../api/errors";
import type {
  AccessScope,
  AdminUser,
  Membership,
  Permission,
  Role,
  UserInput,
} from "../entities/access";
import { LanguageSelect, TimezoneSelect } from "./ProfileFields";
import { formatPhone } from "./formatting";
import { UserAccessSection } from "./UserAccessSection";
import { SubstitutionSection } from "./SubstitutionSection";
import { CloseButton } from "./CloseButton";
import { shellBootstrapKey } from "./bootstrap";
import type { ShellServices } from "./services";
import { EmptyState, LoadingPage } from "./states";
import { Select } from "./Select";
import { Notice } from "./Notice";

type UserStatus = AdminUser["status"];
type LifecycleAction = "block" | "unblock" | "disable" | "archive" | "restore";
type Notice = { tone: "success" | "error"; text: string } | null;
type UserFormValues = {
  first_name: string;
  last_name: string;
  display_name: string;
  email: string;
  phone: string;
  language: string;
  timezone: string;
  password: string;
  password_confirmation: string;
};

function isAccessScope(value: unknown): value is AccessScope {
  if (value === null || typeof value !== "object") return false;
  const scope = value as Record<string, unknown>;
  return (
    typeof scope.id === "string" &&
    typeof scope.code === "string" &&
    typeof scope.name === "string" &&
    typeof scope.is_active === "boolean"
  );
}

const statuses: Array<{ value: UserStatus; label: string }> = [
  { value: "active", label: "Активен" },
  { value: "pending_activation", label: "Ожидает активации" },
  { value: "invited", label: "Приглашён" },
  { value: "blocked", label: "Заблокирован" },
  { value: "disabled", label: "Деактивирован" },
  { value: "business_trip", label: "Командировка" },
  { value: "vacation", label: "Отпуск" },
  { value: "sick_leave", label: "Больничный" },
  { value: "terminated", label: "Уволен" },
  { value: "archived", label: "В архиве" },
];

function statusLabel(status: UserStatus): string {
  return (
    statuses.find((candidate) => candidate.value === status)?.label ?? status
  );
}

/**
 * Ответы проверки приходят с сервера по-английски: там они часть контракта
 * API. Здесь их переводим на человеческий язык — те, что люди видят чаще
 * всего.
 */
function validationText(message: string): string {
  if (/email has already been taken/i.test(message)) {
    return "Сотрудник с такой почтой уже заведён. Если его архивировали, найдите его через «Показывать архив» и восстановите — заводить второго с той же почтой нельзя.";
  }
  if (/must be a valid email/i.test(message)) {
    return "Проверьте адрес почты: он записан с ошибкой.";
  }
  if (/timezone/i.test(message)) {
    return "Выберите часовой пояс из списка.";
  }
  return message;
}

function apiMessage(error: unknown): string {
  if (isApiClientError(error)) {
    if (error.status === 409)
      return "Конфликт изменений: данные уже обновлены другим пользователем. Проверьте актуальную карточку и повторите действие.";
    if (error.status === 422) {
      const details = error.details as { fields?: Record<string, string[]> };
      const messages = Object.values(details.fields ?? {}).flat();
      return messages[0] === undefined
        ? "Сервер отклонил введённые данные. Повторите попытку."
        : validationText(messages[0]);
    }
    if (error.status === 403) return "Недостаточно прав для этого действия.";
  }
  return "Операция не выполнена. Повторите попытку.";
}

function initials(user: AdminUser): string {
  return user.display_name
    .split(/\s+/)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function UserAvatar({
  user,
  files,
  large = false,
}: {
  user: AdminUser;
  files: ShellServices["files"];
  large?: boolean;
}) {
  const [failedPath, setFailedPath] = useState<string | null>(null);
  const fileId = user.avatar?.file_id;
  const download = useQuery({
    queryKey: ["files", fileId, "download"],
    queryFn: () => files.download(fileId ?? ""),
    enabled: fileId !== undefined,
    staleTime: 60_000,
  });
  const source = download.data?.data.url;
  const canShowImage = source !== undefined && source !== failedPath;

  return (
    <span className={`person-avatar${large ? " person-avatar--large" : ""}`}>
      {canShowImage ? (
        <img src={source} alt="" onError={() => setFailedPath(source)} />
      ) : (
        initials(user)
      )}
    </span>
  );
}

/** Рабочие состояния сотрудника: они не про доступ, а про присутствие. */
const workStates: ReadonlyArray<{ value: AdminUser["status"]; label: string }> =
  [
    { value: "active", label: "На месте" },
    { value: "business_trip", label: "В командировке" },
    { value: "vacation", label: "В отпуске" },
    { value: "sick_leave", label: "На больничном" },
  ];

function allowedActions(
  user: AdminUser,
): Array<{ action: LifecycleAction; label: string; critical: boolean }> {
  switch (user.status) {
    case "active":
    case "pending_activation":
    case "invited":
    case "business_trip":
    case "vacation":
    case "sick_leave":
      return [
        { action: "block", label: "Заблокировать", critical: true },
        { action: "disable", label: "Деактивировать", critical: true },
        { action: "archive", label: "Архивировать", critical: true },
      ];
    case "blocked":
      return [
        { action: "unblock", label: "Разблокировать", critical: true },
        { action: "archive", label: "Архивировать", critical: true },
      ];
    case "disabled":
    case "terminated":
      return [{ action: "archive", label: "Архивировать", critical: true }];
    case "archived":
      return [{ action: "restore", label: "Восстановить", critical: true }];
  }
}

function date(value: string | null): string {
  return value === null
    ? "—"
    : new Intl.DateTimeFormat("ru-RU", {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(new Date(value));
}

type UsersSearch = { user?: string };

// eslint-disable-next-line react-refresh/only-export-components -- маршрут использует валидатор query string.
export function validateUsersSearch(
  value: Record<string, unknown>,
): UsersSearch {
  const candidate = value.user;
  return typeof candidate === "string" && candidate.trim()
    ? { user: candidate }
    : {};
}

/**
 * Страница пользователей внутри маршрута.
 *
 * Карточку сотрудника открывают ссылкой из других разделов — из состава
 * организации, из списка исполнителей, — поэтому выбранный человек живёт в
 * адресе. Сама страница про адрес ничего не знает и получает выбор пропсами.
 */
export function UsersRoute({ services }: { services: ShellServices }) {
  const routeSearch: UsersSearch = useSearch({
    from: "/authenticated/administration/users/list",
  });
  const navigate = useNavigate({ from: "/administration/users/list" });

  return (
    <UserManagementPage
      services={services}
      selectedUserId={routeSearch.user ?? null}
      onSelectUser={(value) =>
        void navigate({ search: value ? { user: value } : {}, replace: true })
      }
    />
  );
}

export function UserManagementPage({
  services,
  selectedUserId,
  onSelectUser,
}: {
  services: ShellServices;
  selectedUserId?: string | null;
  onSelectUser?: (value: string | null) => void;
}) {
  const client = useQueryClient();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<UserStatus | "">("");
  const [scopeId, setScopeId] = useState("");
  const [archive, setArchive] = useState(false);
  const [page, setPage] = useState(1);
  const [localSelected, setLocalSelected] = useState<string | null>(null);
  const selected =
    selectedUserId !== undefined ? selectedUserId : localSelected;
  const setSelected = onSelectUser ?? setLocalSelected;
  const [mode, setMode] = useState<"invite" | "create" | null>(null);
  const [notice, setNotice] = useState<Notice>(null);

  // Подразделение бэкенд определяет по членству пользователя, поэтому клиент
  // его не передаёт и не повторяет эту логику у себя.
  const users = useQuery({
    queryKey: ["users", { search, status, scopeId, archive, page }],
    queryFn: () =>
      services.administration.listUsers({
        page,
        per_page: 25,
        ...(search ? { search } : {}),
        ...(status ? { status } : {}),
        ...(scopeId ? { scope: scopeId } : {}),
        ...(archive ? { with_deleted: true } : {}),
      }),
  });
  // Организации собираем из тех, к кому привязаны сотрудники в списках. Список
  // накапливается: после выбора КПО сервер вернёт только его сотрудников, и без
  // накопления остальные варианты пропали бы из выпадающего списка.
  const [knownScopes, setKnownScopes] = useState<Map<string, AccessScope>>(
    () => new Map(),
  );
  const loadedUsers = users.data?.data;
  useEffect(() => {
    if (loadedUsers === undefined) return;
    setKnownScopes((current) => {
      const next = new Map(current);
      for (const user of loadedUsers)
        for (const scope of user.scopes.filter(isAccessScope))
          next.set(scope.id, scope);
      return next.size === current.size ? current : next;
    });
  }, [loadedUsers]);
  const scopeOptions = [...knownScopes.values()].sort((a, b) =>
    a.name.localeCompare(b.name, "ru"),
  );

  const invalidateUsers = async () =>
    client.invalidateQueries({ queryKey: ["users"] });
  const returnFromArchive = () => {
    setArchive(false);
    setPage(1);
  };

  if (users.isPending) return <LoadingPage label="Загружаем пользователей" />;
  if (users.isError)
    return (
      <section className="workspace-page">
        <EmptyState
          title="Не удалось загрузить пользователей"
          description={apiMessage(users.error)}
          action={
            archive
              ? { label: "Вернуться назад", onClick: returnFromArchive }
              : undefined
          }
        />
      </section>
    );
  const list = users.data.data;
  const meta = users.data.meta;

  return (
    <section
      className="workspace-page users-page"
      aria-labelledby="users-title"
    >
      <div className="page-heading page-heading--split">
        <div>
          <p className="eyebrow">Пользователи</p>
          <h1 id="users-title">Сотрудники</h1>
          <p>
            Управляйте доступом, статусом и безопасностью сотрудников в
            выбранном КПО.
          </p>
        </div>
        <div className="page-actions">
          <button
            className="button button--secondary"
            type="button"
            onClick={() => setMode("create")}
          >
            Создать
          </button>
          <button
            className="button button--primary"
            type="button"
            onClick={() => setMode("invite")}
          >
            Пригласить
          </button>
        </div>
      </div>
      {notice ? (
        <div
          className={`operation-notice operation-notice--${notice.tone}`}
          role="status"
        >
          {notice.text}
        </div>
      ) : null}
      <div className="users-toolbar" aria-label="Фильтры пользователей">
        <label>
          <span>Поиск</span>
          <input
            value={search}
            placeholder="Имя или email"
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
          />
        </label>
        <label>
          <span>Статус</span>
          <Select
            value={status}
            onChange={(event) => {
              setStatus(event.target.value as UserStatus | "");
              setPage(1);
            }}
          >
            <option value="">Все статусы</option>
            {statuses.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </Select>
        </label>
        <label>
          <span>КПО</span>
          <Select
            value={scopeId}
            onChange={(event) => {
              setScopeId(event.target.value);
              setPage(1);
            }}
          >
            <option value="">Все КПО</option>
            {scopeOptions.map((scope) => (
              <option key={scope.id} value={scope.id}>
                {scope.name}
              </option>
            ))}
          </Select>
        </label>
        <label className="checkbox-field">
          <input
            aria-label="Показывать архив"
            type="checkbox"
            checked={archive}
            onChange={(event) => {
              setArchive(event.target.checked);
              setPage(1);
            }}
          />{" "}
          <span>Показывать архив</span>
        </label>
      </div>
      <div className="users-table-wrap">
        <table className="users-table">
          <thead>
            <tr>
              <th>Пользователь</th>
              <th>Статус</th>
              <th>Должность и КПО</th>
              <th>Последний вход</th>
              <th>
                <span className="visually-hidden">Открыть</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {list.map((user) => (
              <tr key={user.id}>
                <td>
                  <button
                    type="button"
                    className="user-cell"
                    onClick={() => setSelected(user.id)}
                  >
                    <UserAvatar user={user} files={services.files} />
                    <span>
                      <strong>{user.display_name}</strong>
                      <small>{user.email}</small>
                    </span>
                  </button>
                </td>
                <td>
                  <span className={`status-badge status-badge--${user.status}`}>
                    {statusLabel(user.status)}
                  </span>
                </td>
                <td>
                  <span>{user.position ?? "Не указана"}</span>
                  <small>{user.department ?? "Без подразделения"}</small>
                </td>
                <td>{date(user.last_login_at)}</td>
                <td>
                  <button
                    className="button button--secondary button--compact"
                    type="button"
                    onClick={() => setSelected(user.id)}
                  >
                    Карточка
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {list.length === 0 ? (
          <EmptyState
            title={
              archive
                ? "В архиве пока нет пользователей"
                : "Пользователи не найдены"
            }
            description={
              archive
                ? "Вернитесь к активным пользователям или измените фильтры."
                : "Измените поиск или фильтры, чтобы увидеть сотрудников."
            }
            action={
              archive
                ? { label: "Вернуться назад", onClick: returnFromArchive }
                : undefined
            }
          />
        ) : null}
      </div>
      <nav className="pagination" aria-label="Пагинация пользователей">
        <span>Всего: {meta.total}</span>
        <button
          className="button button--secondary button--compact"
          type="button"
          disabled={page <= 1}
          onClick={() => setPage((value) => value - 1)}
        >
          Назад
        </button>
        <span>
          Страница {meta.current_page} из {meta.last_page}
        </span>
        <button
          className="button button--secondary button--compact"
          type="button"
          disabled={page >= meta.last_page}
          onClick={() => setPage((value) => value + 1)}
        >
          Далее
        </button>
      </nav>
      {mode ? (
        <UserForm
          mode={mode}
          onClose={() => setMode(null)}
          onSuccess={async (text) => {
            setMode(null);
            setNotice({ tone: "success", text });
            await invalidateUsers();
          }}
          service={services.administration}
        />
      ) : null}
      {selected ? (
        <UserDrawer
          userId={selected}
          services={services}
          onClose={() => setSelected(null)}
          onChanged={async (text) => {
            setNotice({ tone: "success", text });
            await invalidateUsers();
          }}
        />
      ) : null}
    </section>
  );
}

function UserForm({
  mode,
  service,
  onClose,
  onSuccess,
}: {
  mode: "invite" | "create";
  service: ShellServices["administration"];
  onClose: () => void;
  onSuccess: (message: string) => Promise<void>;
}) {
  const [values, setValues] = useState<UserFormValues>({
    first_name: "",
    last_name: "",
    display_name: "",
    email: "",
    phone: "",
    language: "ru",
    timezone: "Asia/Barnaul",
    password: "",
    password_confirmation: "",
  });
  const [error, setError] = useState<string | null>(null);
  const mutation = useMutation({
    mutationFn: async () => {
      const base = {
        first_name: values.first_name,
        last_name: values.last_name,
        ...(values.display_name ? { display_name: values.display_name } : {}),
        email: values.email,
        ...(values.phone ? { phone: values.phone } : {}),
        language: values.language,
        timezone: values.timezone,
      };
      return mode === "invite"
        ? service.inviteUser(base)
        : service.createUser({
            ...base,
            password: values.password,
            password_confirmation: values.password_confirmation,
          } as UserInput);
    },
    onSuccess: () =>
      onSuccess(
        mode === "invite"
          ? "Приглашение создано и зафиксировано в истории пользователя."
          : "Пользователь создан.",
      ),
    onError: (cause) => setError(apiMessage(cause)),
  });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    mutation.mutate();
  };
  return (
    <Modal
      title={
        mode === "invite" ? "Пригласить пользователя" : "Создать пользователя"
      }
      onClose={onClose}
    >
      <form className="user-form" onSubmit={submit}>
        <FormFields values={values} setValues={setValues} />
        {mode === "create" ? (
          <>
            <label>
              <span>Пароль</span>
              <input
                aria-label="Пароль"
                type="password"
                minLength={8}
                required
                value={values.password}
                onChange={(event) =>
                  setValues({ ...values, password: event.target.value })
                }
              />
            </label>
            <label>
              <span>Подтверждение пароля</span>
              <input
                aria-label="Подтверждение пароля"
                type="password"
                minLength={8}
                required
                value={values.password_confirmation}
                onChange={(event) =>
                  setValues({
                    ...values,
                    password_confirmation: event.target.value,
                  })
                }
              />
            </label>
          </>
        ) : null}
        {error ? <Notice>{error}</Notice> : null}
        <div className="modal-actions">
          <button
            className="button button--secondary"
            type="button"
            onClick={onClose}
          >
            Отмена
          </button>
          <button
            className="button button--primary"
            disabled={mutation.isPending}
            type="submit"
          >
            {mutation.isPending
              ? "Сохраняем…"
              : mode === "invite"
                ? "Отправить приглашение"
                : "Создать"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function FormFields({
  values,
  setValues,
}: {
  values: UserFormValues;
  setValues: Dispatch<SetStateAction<UserFormValues>>;
}) {
  const update = (key: keyof typeof values, value: string) =>
    setValues({ ...values, [key]: value });
  return (
    <>
      <label>
        <span>Имя</span>
        <input
          required
          value={values.first_name}
          onChange={(event) => update("first_name", event.target.value)}
        />
      </label>
      <label>
        <span>Фамилия</span>
        <input
          required
          value={values.last_name}
          onChange={(event) => update("last_name", event.target.value)}
        />
      </label>
      <label>
        <span>Отображаемое имя</span>
        <input
          value={values.display_name}
          onChange={(event) => update("display_name", event.target.value)}
        />
      </label>
      <label>
        <span>Email</span>
        <input
          type="email"
          required
          value={values.email}
          onChange={(event) => update("email", event.target.value)}
        />
      </label>
      <label>
        <span>Телефон</span>
        <input
          type="tel"
          inputMode="tel"
          placeholder="+7 (999) 123-45-67"
          value={values.phone}
          onChange={(event) => update("phone", formatPhone(event.target.value))}
        />
      </label>
      <label>
        <span>Язык</span>
        <LanguageSelect
          value={values.language}
          onChange={(value) => update("language", value)}
        />
      </label>
      <label>
        <span>Часовой пояс</span>
        <TimezoneSelect
          value={values.timezone}
          onChange={(value) => update("timezone", value)}
        />
      </label>
    </>
  );
}

function UserMembershipSection({
  user,
  services,
  onChanged,
}: {
  user: AdminUser;
  services: ShellServices;
  onChanged: (message: string) => Promise<void>;
}) {
  const client = useQueryClient();
  const userId = user.id;
  const userScopes = user.scopes.filter(isAccessScope);
  const [scopeId, setScopeId] = useState("");
  const [departmentId, setDepartmentId] = useState("");
  const [notice, setNotice] = useState<Notice>(null);
  const scopes = useQuery({
    queryKey: ["scopes", "available"],
    queryFn: () => services.administration.listScopes(),
  });
  const departments = useQuery({
    queryKey: ["scope", scopeId, "departments"],
    queryFn: () => services.administration.listDepartments(scopeId),
    enabled: Boolean(scopeId),
  });
  const memberships = useQuery({
    queryKey: [
      "user",
      userId,
      "memberships",
      userScopes.map((scope) => scope.id),
    ],
    queryFn: async () => {
      // Организации сотрудника перебираются по одной, и на какую-то из них
      // прав может не быть. Тогда показываем остальные, а не рушим весь
      // список: иначе карточка целиком превращалась в сообщение об ошибке.
      const results = await Promise.allSettled(
        userScopes.map(async (scope) => ({
          scope,
          memberships: await services.administration.listMemberships(scope.id),
        })),
      );
      return results
        .filter((result) => result.status === "fulfilled")
        .flatMap(({ value: { scope, memberships: items } }) =>
          items
            .filter((membership) => membership.user_id === userId)
            .map((membership) => ({ scope, membership })),
        );
    },
  });
  const membershipDepartments = useQueries({
    queries: userScopes.map((scope) => ({
      queryKey: ["scope", scope.id, "departments"],
      queryFn: () => services.administration.listDepartments(scope.id),
    })),
  });
  const membershipDepartmentName = (
    scopeId: string,
    departmentId: string | null,
  ) => {
    if (!departmentId) return null;
    const index = userScopes.findIndex((scope) => scope.id === scopeId);
    return membershipDepartments[index]?.data?.find(
      (department) => department.id === departmentId,
    )?.name;
  };
  const addMembership = useMutation({
    mutationFn: () =>
      services.administration.saveMembership(scopeId, {
        user_id: userId,
        department_id: departmentId || null,
      }),
    onSuccess: async () => {
      const selectedScope = scopes.data?.find((scope) => scope.id === scopeId);
      const text = `Пользователь добавлен в «${selectedScope?.name ?? "выбранный контекст"}» без назначения прав.`;
      setDepartmentId("");
      await Promise.all([
        client.invalidateQueries({ queryKey: ["scope", scopeId] }),
        client.invalidateQueries({ queryKey: ["users"] }),
        client.invalidateQueries({ queryKey: ["user", userId] }),
        client.invalidateQueries({ queryKey: shellBootstrapKey }),
      ]);
      await onChanged(text);
    },
    onError: (error) => setNotice({ tone: "error", text: apiMessage(error) }),
  });
  const removeMembership = useMutation({
    mutationFn: ({
      scope,
      membership,
    }: {
      scope: AccessScope;
      membership: Membership;
    }) => services.administration.deleteMembership(scope.id, membership.id),
    onSuccess: async (_, { scope }) => {
      const text = `Пользователь удалён из «${scope.name}».`;
      await Promise.all([
        client.invalidateQueries({ queryKey: ["scope", scope.id] }),
        client.invalidateQueries({ queryKey: ["user", userId, "memberships"] }),
        client.invalidateQueries({ queryKey: ["users"] }),
        client.invalidateQueries({ queryKey: ["user", userId] }),
        client.invalidateQueries({ queryKey: ["access"] }),
        client.invalidateQueries({ queryKey: shellBootstrapKey }),
      ]);
      await onChanged(text);
    },
    onError: (error) => setNotice({ tone: "error", text: apiMessage(error) }),
  });
  const selectedScopeAlreadyAssigned = memberships.data?.some(
    ({ scope, membership }) =>
      scope.id === scopeId &&
      membership.department_id === (departmentId || null),
  );

  return (
    <section className="drawer-section">
      <h3>Состав в организации</h3>
      <p>
        Добавьте пользователя в организацию или одно из КПО. Один пользователь
        может состоять в нескольких КПО; роли и права этим действием не
        выдаются.
      </p>
      {notice ? (
        <p
          className={`operation-notice operation-notice--${notice.tone}`}
          role="status"
        >
          {notice.text}
        </p>
      ) : null}
      {memberships.isPending ? (
        <p className="membership-summary__empty">
          Загружаем организации пользователя…
        </p>
      ) : null}
      {memberships.isError ? (
        <Notice>Не удалось загрузить список организаций пользователя.</Notice>
      ) : null}
      {memberships.data?.length ? (
        <ul
          className="membership-summary"
          aria-label="Организации пользователя"
        >
          {memberships.data.map(({ scope, membership }) => (
            <li key={membership.id}>
              <span>
                <strong>{scope.name}</strong>
                <small>
                  {scope.type === "core" ? "Организация" : "КПО"}
                  {membershipDepartmentName(scope.id, membership.department_id)
                    ? ` · ${membershipDepartmentName(scope.id, membership.department_id)}`
                    : ""}
                  {membership.position ? ` · ${membership.position}` : ""}
                </small>
              </span>
              <button
                className="button button--text membership-summary__remove"
                type="button"
                disabled={removeMembership.isPending}
                onClick={() => removeMembership.mutate({ scope, membership })}
                aria-label={`Удалить пользователя из ${scope.name}`}
              >
                Удалить
              </button>
            </li>
          ))}
        </ul>
      ) : !memberships.isPending && !memberships.isError ? (
        <p className="membership-summary__empty">
          Пользователь пока не добавлен ни в одну организацию или КПО.
        </p>
      ) : null}
      <form
        className="membership-form"
        onSubmit={(event) => {
          event.preventDefault();
          addMembership.mutate();
        }}
      >
        <label>
          <span>Организация или КПО</span>
          <Select
            required
            value={scopeId}
            disabled={scopes.isPending || scopes.isError}
            onChange={(event) => {
              setScopeId(event.target.value);
              setDepartmentId("");
            }}
          >
            <option value="">Выберите контекст</option>
            {scopes.data?.map((scope) => (
              <option key={scope.id} value={scope.id}>
                {scope.name}
              </option>
            ))}
          </Select>
        </label>
        <label>
          <span>Подразделение</span>
          <Select
            value={departmentId}
            disabled={!scopeId || departments.isPending || departments.isError}
            onChange={(event) => setDepartmentId(event.target.value)}
          >
            <option value="">Без подразделения</option>
            {departments.data?.map((department) => (
              <option key={department.id} value={department.id}>
                {department.name}
              </option>
            ))}
          </Select>
        </label>
        <button
          className="button button--primary"
          type="submit"
          disabled={
            !scopeId || addMembership.isPending || selectedScopeAlreadyAssigned
          }
        >
          {addMembership.isPending
            ? "Добавляем…"
            : selectedScopeAlreadyAssigned
              ? "Уже в составе"
              : "Добавить в состав"}
        </button>
      </form>
      {scopes.isError ? (
        <Notice>Не удалось загрузить доступные контексты.</Notice>
      ) : null}
    </section>
  );
}

function UserDrawer({
  userId,
  services,
  onClose,
  onChanged,
}: {
  userId: string;
  services: ShellServices;
  onClose: () => void;
  onChanged: (message: string) => Promise<void>;
}) {
  const client = useQueryClient();
  const [confirm, setConfirm] = useState<{
    action: LifecycleAction;
    label: string;
  } | null>(null);
  const [notice, setNotice] = useState<Notice>(null);
  const detail = useQuery({
    queryKey: ["user", userId],
    queryFn: () => services.administration.user(userId),
  });
  const history = useQuery({
    queryKey: ["user", userId, "history"],
    queryFn: () => services.administration.history(userId),
    enabled: detail.data !== undefined,
  });
  const sessions = useQuery({
    queryKey: ["user", userId, "sessions"],
    queryFn: () => services.administration.sessions(userId),
    enabled: detail.data !== undefined,
  });
  const roles = useQuery<Role[]>({
    queryKey: ["access", "roles"],
    queryFn: () => services.access!.listRoles(),
    enabled: detail.data !== undefined && services.access !== undefined,
  });
  const permissions = useQuery<Permission[]>({
    queryKey: ["access", "permissions"],
    queryFn: () => services.access!.permissions(),
    enabled: detail.data !== undefined && services.access !== undefined,
  });
  const refresh = async () => {
    await client.invalidateQueries({ queryKey: ["user", userId] });
  };
  /**
   * Где сейчас сотрудник: на месте, в отпуске, в командировке, на больничном.
   *
   * Это не блокировка и не увольнение — человек остаётся в системе, меняется
   * только то, как его видно коллегам. Раньше такие состояния были в фильтрах
   * и в списке, а поставить их было нечем.
   */
  const workState = useMutation({
    mutationFn: (status: AdminUser["status"]) =>
      services.administration.setUserStatus(userId, status),
    onSuccess: async (updated) => {
      const text = `Статус изменён: ${statusLabel(updated.status)}.`;
      setNotice({ tone: "success", text });
      await onChanged(text);
      await refresh();
    },
    onError: (error) => setNotice({ tone: "error", text: apiMessage(error) }),
  });
  const transition = useMutation({
    mutationFn: ({ action }: { action: LifecycleAction }) =>
      services.administration.transitionUser(userId, action),
    onSuccess: async (user, { action }) => {
      setConfirm(null);
      const text =
        action === "block"
          ? `Пользователь заблокирован. Активные сессии завершены сервером; новый статус: ${statusLabel(user.status)}.`
          : `Статус изменён: ${statusLabel(user.status)}.`;
      setNotice({ tone: "success", text });
      await onChanged(text);
      await refresh();
    },
    onError: (error) => setNotice({ tone: "error", text: apiMessage(error) }),
  });
  const twoFactor = useMutation({
    mutationFn: (required: boolean) =>
      services.administration.requireTwoFactor(userId, required),
    onSuccess: async () => {
      setNotice({
        tone: "success",
        text: "Требование двухфакторной аутентификации обновлено.",
      });
      await refresh();
    },
    onError: (error) => setNotice({ tone: "error", text: apiMessage(error) }),
  });
  const avatar = useMutation({
    mutationFn: (fileId: string) =>
      services.administration.assignAvatar(userId, fileId),
    onSuccess: async () => {
      setNotice({ tone: "success", text: "Аватар пользователя обновлён." });
      await refresh();
    },
    onError: (error) => setNotice({ tone: "error", text: apiMessage(error) }),
  });
  const removeAvatar = useMutation({
    mutationFn: () => services.administration.removeAvatar(userId),
    onSuccess: async () => {
      setNotice({ tone: "success", text: "Аватар удалён." });
      await refresh();
    },
    onError: (error) => setNotice({ tone: "error", text: apiMessage(error) }),
  });
  const uploadAvatar = useMutation({
    mutationFn: async (file: File) => {
      if (!/^image\/(jpeg|png|webp)$/.test(file.type))
        throw new Error("Поддерживаются JPEG, PNG и WebP.");
      const upload = await services.files.initiate({
        purpose: "avatar",
        filename: file.name,
        mime_type: file.type,
        size: file.size,
      });
      await services.files.uploadObject(upload.upload, file);
      await services.files.confirm(upload.data.id);
      return services.administration.assignAvatar(userId, upload.data.id);
    },
    onSuccess: async () => {
      setNotice({
        tone: "success",
        text: "Аватар загружен с компьютера и обновлён.",
      });
      await refresh();
    },
    onError: (error) => setNotice({ tone: "error", text: apiMessage(error) }),
  });
  if (detail.isPending)
    return (
      <aside className="user-drawer">
        <LoadingPage label="Открываем карточку" />
      </aside>
    );
  if (detail.isError)
    return (
      <Modal title="Карточка пользователя" onClose={onClose}>
        <Notice>{apiMessage(detail.error)}</Notice>
      </Modal>
    );
  const user = detail.data;
  return (
    <>
      <aside
        className="user-drawer"
        aria-label={`Карточка ${user.display_name}`}
      >
        <div className="drawer-head">
          <div className="person-heading">
            <UserAvatar user={user} files={services.files} large />
            <div>
              <p className="eyebrow">Карточка пользователя</p>
              <h2>{user.display_name}</h2>
              <span className={`status-badge status-badge--${user.status}`}>
                {statusLabel(user.status)}
              </span>
            </div>
          </div>
          <CloseButton label="Закрыть карточку" onClick={onClose} />
        </div>
        {notice ? (
          <div
            className={`operation-notice operation-notice--${notice.tone}`}
            role="status"
          >
            {notice.text}
          </div>
        ) : null}
        <section className="drawer-section">
          <h3>Профиль</h3>
          <dl>
            <div>
              <dt>Email</dt>
              <dd>{user.email}</dd>
            </div>
            <div>
              <dt>Телефон</dt>
              <dd>{user.phone ?? "—"}</dd>
            </div>
            <div>
              <dt>Должность</dt>
              <dd>{user.position ?? "—"}</dd>
            </div>
            <div>
              <dt>Подразделение</dt>
              <dd>{user.department ?? "—"}</dd>
            </div>
            <div>
              <dt>Язык</dt>
              <dd>{user.language}</dd>
            </div>
            <div>
              <dt>Часовой пояс</dt>
              <dd>{user.timezone}</dd>
            </div>
          </dl>
        </section>
        <UserMembershipSection
          user={user}
          services={services}
          onChanged={onChanged}
        />
        <section className="drawer-section">
          <h3>Жизненный цикл</h3>
          <p>
            Доступны только переходы, разрешённые для текущего статуса.
            Неактуальные действия скрыты.
          </p>
          <div className="action-grid">
            {allowedActions(user).map((item) => (
              <button
                key={item.action}
                className="button button--secondary"
                type="button"
                disabled={transition.isPending}
                onClick={() =>
                  setConfirm({ action: item.action, label: item.label })
                }
              >
                {item.label}
              </button>
            ))}
          </div>
          {workStates.some((item) => item.value === user.status) ||
          user.status === "active" ? (
            <label className="field">
              <span>Где сейчас сотрудник</span>
              <Select
                value={
                  workStates.some((item) => item.value === user.status)
                    ? user.status
                    : "active"
                }
                disabled={workState.isPending}
                onChange={(event) =>
                  workState.mutate(event.target.value as AdminUser["status"])
                }
              >
                {workStates.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </Select>
            </label>
          ) : null}
        </section>
        {services.substitutions ? <SubstitutionSection userId={user.id} scopes={user.scopes} services={services} /> : null}
        <section className="drawer-section">
          <h3>Безопасность</h3>
          <p>
            2FA:{" "}
            {user.two_factor.required
              ? "обязательна"
              : user.two_factor.enabled
                ? "включена пользователем"
                : "не требуется"}
          </p>
          <button
            className="button button--secondary"
            type="button"
            disabled={twoFactor.isPending}
            onClick={() => twoFactor.mutate(!user.two_factor.required)}
          >
            {user.two_factor.required
              ? "Отменить обязательную 2FA"
              : "Сделать 2FA обязательной"}
          </button>
        </section>
        <AvatarSection
          user={user}
          onAssign={(id) => avatar.mutate(id)}
          onUpload={(file) => uploadAvatar.mutate(file)}
          onRemove={() => removeAvatar.mutate()}
          pending={
            avatar.isPending || removeAvatar.isPending || uploadAvatar.isPending
          }
        />
        {services.access !== undefined ? (
          <section className="drawer-section">
            {roles.isError || permissions.isError ? (
              <Notice>
                Не удалось загрузить роли и каталог прав. Закройте карточку и
                попробуйте открыть её снова.
              </Notice>
            ) : (
              <UserAccessSection
                user={user}
                roles={roles.data ?? []}
                permissions={permissions.data ?? []}
                services={services}
              />
            )}
          </section>
        ) : null}
        <section className="drawer-section">
          <h3>Активные сессии</h3>
          {sessions.data?.data.length ? (
            <ul className="activity-list">
              {sessions.data.data.map((session) => (
                <li key={session.id}>
                  <span>
                    <strong>
                      {session.current
                        ? "Текущая сессия"
                        : (session.ip_address ?? "Неизвестный IP")}
                    </strong>
                    <small>
                      {session.user_agent} · {date(session.last_activity_at)}
                    </small>
                  </span>
                  {!session.current ? (
                    <button
                      className="button button--secondary button--compact"
                      type="button"
                      onClick={() =>
                        void services.administration
                          .endSession(user.id, session.id)
                          .then(refresh)
                      }
                    >
                      Завершить
                    </button>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : (
            <p>Активных сессий нет.</p>
          )}
        </section>
        <section className="drawer-section">
          <h3>История изменений</h3>
          <a
            className="button button--secondary button--compact"
            href={`/administration/audit?actor=${encodeURIComponent(user.id)}`}
          >
            Открыть аудит пользователя
          </a>
          {history.data?.data.length ? (
            <ul className="activity-list">
              {history.data.data.map((change) => (
                <li key={change.id}>
                  <span>
                    <strong>{change.action}</strong>
                    <small>
                      {date(change.created_at)} · request:{" "}
                      {change.request_id ?? "—"}
                    </small>
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p>Записей истории пока нет.</p>
          )}
        </section>
      </aside>
      {confirm ? (
        <Modal title={confirm.label} onClose={() => setConfirm(null)}>
          <p>
            Подтвердите критическое действие для «{user.display_name}».
            Результат будет зафиксирован в истории пользователя.
          </p>
          <div className="modal-actions">
            <button
              className="button button--secondary"
              type="button"
              onClick={() => setConfirm(null)}
            >
              Отмена
            </button>
            <button
              className="button button--primary"
              type="button"
              disabled={transition.isPending}
              onClick={() => transition.mutate({ action: confirm.action })}
            >
              {transition.isPending ? "Выполняем…" : "Подтвердить"}
            </button>
          </div>
        </Modal>
      ) : null}
    </>
  );
}

function AvatarSection({
  user,
  onAssign,
  onUpload,
  onRemove,
  pending,
}: {
  user: AdminUser;
  onAssign: (fileId: string) => void;
  onUpload: (file: File) => void;
  onRemove: () => void;
  pending: boolean;
}) {
  const [fileId, setFileId] = useState("");
  return (
    <section className="drawer-section">
      <h3>Аватар</h3>
      <p>Выберите фото с компьютера или укажите ID уже загруженного файла.</p>
      <div className="inline-form">
        <label className="button button--secondary button--compact">
          <span>Выбрать фото</span>
          <input
            className="visually-hidden"
            type="file"
            accept="image/jpeg,image/png,image/webp"
            disabled={pending}
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) onUpload(file);
              event.currentTarget.value = "";
            }}
          />
        </label>
        <input
          aria-label="ID файла аватара"
          value={fileId}
          placeholder="UUID файла"
          onChange={(event) => setFileId(event.target.value)}
        />
        <button
          className="button button--secondary button--compact"
          type="button"
          disabled={!fileId || pending}
          onClick={() => onAssign(fileId)}
        >
          Сохранить
        </button>
        {user.avatar ? (
          <button
            className="button button--text"
            type="button"
            disabled={pending}
            onClick={onRemove}
          >
            Удалить
          </button>
        ) : null}
      </div>
    </section>
  );
}

/** Окно используют и другие страницы раздела. */
export function Modal({
  title,
  children,
  onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  useEffect(() => {
    const listener = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", listener);
    return () => document.removeEventListener("keydown", listener);
  }, [onClose]);
  return (
    <div className="modal" role="dialog" aria-modal="true" aria-label={title}>
      <button
        className="modal-backdrop"
        aria-label="Закрыть"
        type="button"
        onClick={onClose}
      />
      <div className="modal-card">
        <div className="modal-title">
          <h2>{title}</h2>
          <CloseButton onClick={onClose} />
        </div>
        {children}
      </div>
    </div>
  );
}
